import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CommentResponse, CommentsSliceModel, CreateCommentRequest, UpdateCommentRequest } from "../Comments/state/comments-interface";
import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { API_BASE_URL as baseUri } from "config/env";

export const GetComments = createAsyncThunk<CommentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/CommentsSlice/GetComments',
    async (activityId: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/comments/get-by-entity?entityId=${activityId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CommentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateComment = createAsyncThunk<CommentResponse, CreateCommentRequest, { rejectValue: RejectedValue }>(
    'Slice/CommentsSlice/CreateComment',
    async (createCommentRequest: CreateCommentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/comments/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(createCommentRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CommentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateComment = createAsyncThunk<CommentResponse, UpdateCommentRequest, { rejectValue: RejectedValue }>(
    'Slice/CommentsSlice/UpdateComment',
    async (updateCommentRequest: UpdateCommentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/comments/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(updateCommentRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CommentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteComment = createAsyncThunk<CommentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/CommentsSlice/DeleteComment',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/comments/delete?id=${id}`, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CommentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

const initialValue: CommentsSliceModel = {
    statusCode: "",
    message: "",
    showNotification: false,
    isLoading: false,
    comments: []
}

export const CommentsSlice = createSlice({
    name: "CommentsSlice",
    initialState: initialValue,
    reducers: {
        resetCommentsSliceState: (state) => {
            state.statusCode = "";
            state.message = "";
            state.showNotification = false;
            state.isLoading = false;
            state.comments = [];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(CreateComment.fulfilled, (state, action) => {
                state.isLoading = false;
                state.statusCode = "200";
                state.showNotification = false;
                if (!Array.isArray(state.comments)) {
                    state.comments = [];
                }
                const existingIndex = state.comments.findIndex(c => c.activityId === action.payload.activityId);
                if (existingIndex !== -1) {
                    state.comments[existingIndex] = action.payload;
                } else {
                    state.comments.push(action.payload);
                }
            })
            .addCase(CreateComment.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateComment.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(UpdateComment.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(UpdateComment.fulfilled, (state, action) => {
                state.isLoading = false;
                state.statusCode = "200";
                state.showNotification = true;
                if (!Array.isArray(state.comments)) {
                    state.comments = [];
                }
                const existingIndex = state.comments.findIndex(c => c.activityId === action.payload.activityId);
                if (existingIndex !== -1) {
                    state.comments[existingIndex] = action.payload;
                } else {
                    state.comments.push(action.payload);
                }
            })
            .addCase(UpdateComment.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteComment.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(DeleteComment.fulfilled, (state, action) => {
                state.isLoading = false;
                state.statusCode = "200";
                state.showNotification = true;
                if (!Array.isArray(state.comments)) {
                    state.comments = [];
                }
                const existingIndex = state.comments.findIndex(c => c.activityId === action.payload.activityId);
                if (existingIndex !== -1) {
                    state.comments[existingIndex] = action.payload;
                } else {
                    state.comments.push(action.payload);
                }
            })
            .addCase(DeleteComment.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetComments.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(GetComments.fulfilled, (state, action) => {
                state.isLoading = false;
                state.statusCode = "200";
                state.showNotification = true;
                if (!Array.isArray(state.comments)) {
                    state.comments = [];
                }
                const existingIndex = state.comments.findIndex(c => c.activityId === action.payload.activityId);
                if (existingIndex !== -1) {
                    state.comments[existingIndex] = action.payload;
                } else {
                    state.comments.push(action.payload);
                }
            })
            .addCase(GetComments.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            });
    }
});

export const { resetCommentsSliceState } = CommentsSlice.actions;
export default CommentsSlice.reducer;
